import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import type { SceneModule } from './SceneManager';
import { elementToWorld, elementToWorldSize } from '@core/ScreenToWorld';
import type { PhysicsWorld } from '@physics/PhysicsWorld';
import type { ScrollManager } from '@core/ScrollManager';
import { damp, lerp, clamp, smoothstep } from '@utils/lerp';
import { getUserPrefs } from '@core/UserPrefs';
import { buildBallMesh } from './shared/golfAndTee';

/**
 * HeroScene — Lusion-style 3D golf ball with Rapier physics, mouse-as-club
 * impulse, stencil-clipped viewing window, and live TrackMan-style stats.
 *
 * Architecture (composition over inheritance):
 *  - This module owns: ball mesh + body, mouse cursor collider, stencil mask
 *    mesh, stat-binding state, SVG trail overlay, mobile fallback flag.
 *  - It does NOT own: scene/camera/renderer/physics-world (injected by App).
 *
 * Stencil clipping (technique #4): two passes share the same THREE.Scene but
 * different renderOrder. Pass 1 (renderOrder=1) writes ref=1 into the stencil
 * buffer wherever the rounded-rect mask covers; pass 2 (renderOrder=2) draws
 * the ball with stencilFunc=EQUAL, ref=1 — it ONLY paints where the mask wrote.
 * autoClearStencil is left at its default (true) so the buffer resets each frame.
 *
 * Physics (technique #5): kinematic-position-based body for the cursor (we set
 * its translation directly, Rapier moves it correctly across substeps); dynamic
 * body for the ball with 0 gravity (we want it to float; we apply our own
 * anchor-spring + idle-noise + cursor-amplified impulse).
 *
 * Material (technique #6 simplified): MeshMatcapMaterial cannot be subclassed
 * with arbitrary shader edits without onBeforeCompile pain — instead we build
 * a small custom ShaderMaterial that combines:
 *   - matcap sample by view-space perturbed normal
 *   - procedural dimple normal-map (256-dimple Fibonacci lattice on the surface)
 *   - subtle fresnel rim term for cool-edge separation
 * Result is monochrome pearl/silver with visible dimples — no neon, no rainbow.
 */

// -----------------------------------------------------------------------------
// Tuned constants (each documented with what it controls)
// -----------------------------------------------------------------------------

/** Mouse-velocity → ball-impulse multiplier. Higher = harder hits per pixel. */
const AMPLIFY = 7;
/** EMA smoothing factor for mouse velocity (lower = smoother but laggier). */
const MOUSE_VEL_EMA = 0.25;
/** ms after last mousemove event before we treat the cursor as stationary. */
const MOUSE_IDLE_MS = 50;
/** ms after last ball-cursor contact before we resume idle-noise impulses. */
const ANCHOR_REENGAGE_MS = 800;
/** Anchor ramp begin (ms since hit) — strength is 0 until here so the impulse "wins". */
const ANCHOR_RAMP_START_MS = 200;
/** Anchor ramp end (ms since hit) — strength is 1 from here onward. */
const ANCHOR_RAMP_END_MS = 600;
/** Spring rate that converts position-error → desired velocity (1/s). */
const ANCHOR_SPRING_LAMBDA = 5;
/** Blend rate for current linvel → desired velocity (1/s). Multiplied by anchorStrength ramp. */
const ANCHOR_BLEND_LAMBDA = 6;
/** Deadzone radius (world units) — within this AND under speed threshold ⇒ snap-to-home. */
const ANCHOR_DEADZONE_POS = 0.05;
/** Deadzone speed threshold (world units / s). */
const ANCHOR_DEADZONE_VEL = 0.05;
/** Idle "wind on tee" impulse magnitude. */
const IDLE_NOISE_IMPULSE = 0.0015;
/** Idle noise period (seconds between random impulses). */
const IDLE_NOISE_PERIOD = 0.35;
/** Stat smoothing factor (higher = stats track physics faster, more jittery). */
const STAT_LERP = 0.12;
/** Stat decay-back-to-baseline lambda when idle. */
const STAT_BASELINE_LAMBDA = 0.7;
/** ms threshold for "recent hit" — stats decay to baseline after this. */
const STAT_HOT_MS = 1200;
/** Ball linear-velocity threshold for "launch" classification (m/s, fake units). */
const LAUNCH_THRESHOLD = 3;
/** SVG trail fade-out duration (ms). */
const TRAIL_FADE_MS = 2000;
/** Camera distance the ball lives at (world units in front of camera at z=5). */
const HERO_DEPTH = 5;
/** Ball radius (world units). */
const BALL_RADIUS = 0.5;
/** Cursor-collider radius (world units). */
const CURSOR_RADIUS = 0.3;
/** Baseline TrackMan numbers shown when idle. */
const BASELINE_SPIN = 2840;
const BASELINE_LAUNCH = 11.2;
const BASELINE_CARRY = 262;


// Mobile gate: low-end touch devices skip physics + interaction entirely.
const isMobile =
  typeof window !== 'undefined' &&
  (window.innerWidth < 768 ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches));

// -----------------------------------------------------------------------------
// Rounded-rect mask geometry
// -----------------------------------------------------------------------------

/**
 * Build a flat ShapeGeometry approximating a rounded rectangle of unit size
 * (-0.5..0.5 in x and y). We scale to actual element size each frame.
 */
function buildRoundedRectGeometry(radius: number = 0.06, segments: number = 8): THREE.ShapeGeometry {
  const w = 0.5;
  const h = 0.5;
  const r = Math.min(radius, w, h);
  const shape = new THREE.Shape();
  shape.moveTo(-w + r, -h);
  shape.lineTo(w - r, -h);
  shape.quadraticCurveTo(w, -h, w, -h + r);
  shape.lineTo(w, h - r);
  shape.quadraticCurveTo(w, h, w - r, h);
  shape.lineTo(-w + r, h);
  shape.quadraticCurveTo(-w, h, -w, h - r);
  shape.lineTo(-w, -h + r);
  shape.quadraticCurveTo(-w, -h, -w + r, -h);
  return new THREE.ShapeGeometry(shape, segments);
}

// -----------------------------------------------------------------------------
// HeroScene
// -----------------------------------------------------------------------------

export class HeroScene implements SceneModule {
  readonly name = 'hero';

  private readonly camera: THREE.PerspectiveCamera;
  private readonly physics: PhysicsWorld;
  private readonly scrollManager: ScrollManager;
  /** Frame counter for cheap rate-limiting (rect reads). */
  private frame = 0;

  // Three objects
  private ballMesh!: THREE.Mesh;
  private maskMesh!: THREE.Mesh;
  private ballMaterial!: THREE.ShaderMaterial;
  /** Inner fill sphere material (GLB-only). Free at dispose. */
  private ballFillMaterial: THREE.ShaderMaterial | null = null;
  private matcapTex?: THREE.Texture;

  // DOM references
  private stageEl: HTMLElement | null = null;
  private sphereEl: HTMLElement | null = null;
  private statSpans: HTMLElement[] = [];
  private trailSvg!: SVGSVGElement;
  private trailPath!: SVGPathElement;

  // Physics handles (only set when !isMobile and physics is ready)
  private ballBody?: RAPIER.RigidBody;
  private cursorBody?: RAPIER.RigidBody;

  // State
  private home = new THREE.Vector3();
  private mouseScreen = { x: -1e6, y: -1e6 };
  private lastMouseScreen = { x: 0, y: 0 };
  private mouseVel = { x: 0, y: 0 };
  private lastMouseT = 0;
  /** ms timestamp of most recent ball-cursor contact / hit (drives anchor ramp + stat decay). */
  private lastHitT = 0;
  private idleNoiseAccum = 0;
  private paused = false;

  // Display values (lerped) and current targets.
  private dispSpin = BASELINE_SPIN;
  private dispLaunch = BASELINE_LAUNCH;
  private dispCarry = BASELINE_CARRY;
  private targetSpin = BASELINE_SPIN;
  private targetLaunch = BASELINE_LAUNCH;
  private targetCarry = BASELINE_CARRY;

  // Reusable scratch
  private tmpVec3 = new THREE.Vector3();
  private tmpVec3b = new THREE.Vector3();

  // Cached anchor positions — recomputed every other frame from DOM (the
  // .sphere bobs slightly via CSS animation, so we can't fully cache-on-scroll
  // like Work/Pursuits without visible drift).
  private cachedHome = new THREE.Vector3();
  private cachedStagePos = new THREE.Vector3();
  private cachedStageSize = { width: 0, height: 0 };
  private hasCachedAnchors = false;

  // Bound listeners (for clean dispose)
  private onMouseMove = (e: MouseEvent): void => {
    this.mouseScreen.x = e.clientX;
    this.mouseScreen.y = e.clientY;
    this.lastMouseT = performance.now();
  };
  private onVisibility = (): void => {
    this.paused = document.hidden;
  };

  constructor(
    camera: THREE.PerspectiveCamera,
    physics: PhysicsWorld,
    /** Reserved for future canvas-relative event wiring (touch, pointer-lock). */
    _canvasEl: HTMLCanvasElement,
    scrollManager: ScrollManager
  ) {
    this.camera = camera;
    this.physics = physics;
    this.scrollManager = scrollManager;
    void _canvasEl;
  }

  async init(scene: THREE.Scene): Promise<void> {
    // 1. Resolve DOM anchors. CLAUDE.md explicitly notes that .sphere and
    //    .sphere-stage MUST remain — we read their layout each frame.
    this.stageEl = document.querySelector<HTMLElement>('.sphere-stage');
    this.sphereEl = document.querySelector<HTMLElement>('.sphere-stage .sphere');
    if (!this.stageEl || !this.sphereEl) {
      // Soft-fail: don't crash the rest of the app if the hero markup is gone.
      // eslint-disable-next-line no-console
      console.warn('HeroScene: .sphere-stage / .sphere not found — module disabled');
      return;
    }

    // Stat spans: 3 rows, each row's second <span> is the value.
    const rows = this.stageEl.querySelectorAll<HTMLElement>(
      '.sphere-hud .row > span:nth-child(2)'
    );
    this.statSpans = Array.from(rows);

    // 2. Build SVG trajectory trail overlay (pointer-events: none so it doesn't
    //    eat hover state from .sphere-stage). It lives INSIDE .sphere-stage so
    //    it inherits z-index + position relative to the stage box.
    this.trailSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.trailSvg.setAttribute('class', 'hero-trail-svg');
    this.trailSvg.setAttribute('viewBox', '0 0 100 100');
    this.trailSvg.setAttribute('preserveAspectRatio', 'none');
    Object.assign(this.trailSvg.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      opacity: '0',
      transition: 'opacity 0.25s ease-out',
      zIndex: '2',
    });
    this.trailPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    this.trailPath.setAttribute('fill', 'none');
    this.trailPath.setAttribute('stroke', 'currentColor');
    this.trailPath.setAttribute('stroke-width', '0.4');
    this.trailPath.setAttribute('stroke-linecap', 'round');
    this.trailPath.setAttribute('stroke-dasharray', '120');
    this.trailPath.setAttribute('stroke-dashoffset', '120');
    // currentColor is inherited; pull the brand accent from CSS variable.
    this.trailSvg.style.color = 'var(--accent, #ff6a00)';
    this.trailSvg.appendChild(this.trailPath);
    this.stageEl.appendChild(this.trailSvg);

    // 3. Matcap (loaded from /textures/matcap-pearl.png — Vite serves /public at root).
    const loader = new THREE.TextureLoader();
    this.matcapTex = loader.load('/textures/matcap-pearl.png');
    this.matcapTex.colorSpace = THREE.SRGBColorSpace;
    this.matcapTex.minFilter = THREE.LinearMipmapLinearFilter;
    this.matcapTex.magFilter = THREE.LinearFilter;
    this.matcapTex.generateMipmaps = true;

    // 4. Ball mesh from real GLB geometry (shared with ContactScene via golfBall.ts).
    //    The GLB has dimple geometry baked in, so we let the matcap LUT alone do
    //    the highlight pattern (no procedural normal-map needed). The loader
    //    centers + scales the geometry so its bounding-sphere radius is 0.5,
    //    matching BALL_RADIUS for the existing physics collider + stencil sizing.
    const built = await buildBallMesh(this.matcapTex, {
      rimStrength: 0.45,                       // bumped from 0.35 for cleaner
                                               // silhouette against dark backdrop
      rimColor: new THREE.Color(0x9ec3d6),     // cool-blue rim, very faint
      // The new GLB has real dimple geometry (~1.3M tris). Drop the procedural
      // normal-map perturbation so the matcap pattern follows the actual mesh
      // normals, not a flat-sphere illusion.
      useDimpleMap: false,
      // Soften the matcap lookup toward camera-facing so the deep dimple
      // cavities don't sample the dark edge of the pearl LUT (which used to
      // make the ball read as "hollow / transparent" — issue #1 from user).
      matcapSoftness: 0.55,
    });
    this.ballMesh = built.mesh;
    this.ballMaterial = built.material;

    // Stencil setup (technique #4): ball draws ONLY where mask wrote ref=1.
    // ShaderMaterial doesn't expose stencil props in the constructor — set after.
    this.ballMaterial.stencilWrite = true;
    this.ballMaterial.stencilFunc = THREE.EqualStencilFunc;
    this.ballMaterial.stencilRef = 1;
    this.ballMaterial.stencilZPass = THREE.KeepStencilOp;
    // The GLB builder also returns an inner fill sphere material — apply the
    // same stencil so the white pearl filler stays inside the rounded-rect.
    if (built.fillMaterial) {
      built.fillMaterial.stencilWrite = true;
      built.fillMaterial.stencilFunc = THREE.EqualStencilFunc;
      built.fillMaterial.stencilRef = 1;
      built.fillMaterial.stencilZPass = THREE.KeepStencilOp;
      this.ballFillMaterial = built.fillMaterial;
    }

    this.ballMesh.renderOrder = 2; // after mask
    this.ballMesh.frustumCulled = false; // bbox can be unreliable when we move it via physics
    scene.add(this.ballMesh);

    // 6. Stencil mask mesh: invisible (no color/depth write), writes ref=1
    //    everywhere the rounded rectangle covers. renderOrder=1 → drawn before ball.
    const maskGeom = buildRoundedRectGeometry(0.06, 8);
    const maskMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: false,
      depthTest: false,
      depthWrite: false,
      colorWrite: false,
      stencilWrite: true,
      stencilFunc: THREE.AlwaysStencilFunc,
      stencilRef: 1,
      stencilZPass: THREE.ReplaceStencilOp,
      stencilFail: THREE.ReplaceStencilOp,
      stencilZFail: THREE.ReplaceStencilOp,
      stencilWriteMask: 0xff,
      stencilFuncMask: 0xff,
    });
    this.maskMesh = new THREE.Mesh(maskGeom, maskMat);
    this.maskMesh.renderOrder = 1;
    this.maskMesh.frustumCulled = false;
    scene.add(this.maskMesh);

    // 7. Anchor home position from .sphere element.
    elementToWorld(this.sphereEl, this.camera, HERO_DEPTH, this.home);
    this.ballMesh.position.copy(this.home);

    // 8. Physics setup (skipped on mobile — playbook §8).
    if (!isMobile && this.physics.ready) {
      // Dynamic ball, gravity disabled (we want it to float with anchor spring).
      const ballDesc = RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(this.home.x, this.home.y, this.home.z)
        .setLinearDamping(0.4)
        .setAngularDamping(0.5)
        .setGravityScale(0.0);
      this.ballBody = this.physics.addRigidBody(ballDesc);
      // Hero ball is a SENSOR collider — no physics contact response with any
      // other body in the shared world (Toolkit's SandboxBoundary walls and
      // SkillObject bodies live at world-origin and physically overlap the
      // hero ball's home; without sensor isolation the hero ball pings off
      // those invisible bodies forever, which is the reported sticky-corner
      // jitter). Cursor "contact" is detected by distance check below, not by
      // the physics solver, so we don't need real contact response anyway.
      const ballColDesc = RAPIER.ColliderDesc.ball(BALL_RADIUS)
        .setRestitution(0.6)
        .setDensity(0.5 / ((4 / 3) * Math.PI * BALL_RADIUS ** 3)) // → mass ≈ 0.5
        .setSensor(true);
      this.physics.addCollider(ballColDesc, this.ballBody);

      // Kinematic cursor — we set its translation each frame, Rapier handles
      // continuous-collision detection so swift mouse motions still register.
      const cursorDesc = RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(
        -1000,
        -1000,
        HERO_DEPTH * -1 // way out of the way until first mousemove
      );
      this.cursorBody = this.physics.addRigidBody(cursorDesc);
      // Cursor is also a sensor — same rationale, plus we drive its position
      // each frame and only use it for the distance test below.
      const cursorColDesc = RAPIER.ColliderDesc.ball(CURSOR_RADIUS)
        .setRestitution(0.5)
        .setSensor(true);
      this.physics.addCollider(cursorColDesc, this.cursorBody);
    }

    // 9. Listeners.
    if (!isMobile) {
      window.addEventListener('mousemove', this.onMouseMove, { passive: true });
    }
    document.addEventListener('visibilitychange', this.onVisibility);

    // 10. Tell CSS to hide the .ballart <img> (the existing flat SVG sphere).
    document.documentElement.classList.add('webgl-ready');
  }

  update(dt: number): void {
    if (this.paused || !this.sphereEl || !this.stageEl) return;

    // Perf gate: hero is always at the top of the document. Once the user
    // scrolls past the first ~15% of total page progress, the hero region is
    // off-screen and we can stop ticking entirely. We use the cheaper global
    // scrollProgress (no rect read) instead of sectionProgress('hero').
    // Also HIDE the ball mesh — with a high-poly GLB (~850k tris) leaving it
    // visible costs 4-6× the GPU even when the section is scrolled away.
    if (this.scrollManager.scrollProgress > 0.15) {
      if (this.ballMesh) this.ballMesh.visible = false;
      if (this.maskMesh) this.maskMesh.visible = false;
      return;
    }
    if (this.ballMesh) this.ballMesh.visible = true;
    if (this.maskMesh) this.maskMesh.visible = true;

    this.frame++;

    // 1. Recompute home + stage position from DOM. The .sphere has CSS bob
    //    animation so we can't pure-cache-on-scroll like Work/Pursuits do, but
    //    rate-limiting to every-other-frame halves the cost without visible
    //    drift (the bob period is ~6s; a single skipped frame is invisible).
    if (!this.hasCachedAnchors || this.frame % 2 === 0) {
      elementToWorld(this.sphereEl, this.camera, HERO_DEPTH, this.cachedHome);
      elementToWorld(this.stageEl, this.camera, HERO_DEPTH, this.cachedStagePos);
      const sz = elementToWorldSize(this.stageEl, this.camera, HERO_DEPTH);
      this.cachedStageSize.width = sz.width;
      this.cachedStageSize.height = sz.height;
      this.hasCachedAnchors = true;
    }
    this.home.copy(this.cachedHome);

    // 2. Position the stencil mask at .sphere-stage's bounding box.
    this.maskMesh.position.copy(this.cachedStagePos);
    const stageWorldSize = this.cachedStageSize;
    this.maskMesh.scale.set(stageWorldSize.width, stageWorldSize.height, 1);

    // 3. Mobile path — just rotate slowly and bail. No physics, no stat updates.
    //    Reduced-motion path — keep ball at home rotation 0, write baseline
    //    stats once. Same shape as mobile but without the slow rotation.
    if (isMobile || !this.ballBody) {
      this.ballMesh.position.copy(this.home);
      if (!getUserPrefs().reducedMotion) {
        this.ballMesh.rotation.y += dt * 0.3;
      }
      this.writeStatsToDOM(BASELINE_SPIN, BASELINE_LAUNCH, BASELINE_CARRY);
      return;
    }
    if (getUserPrefs().reducedMotion) {
      // Honour OS prefers-reduced-motion. Keep the ball at home, no impulses,
      // no stats updates — just a static frame.
      this.ballMesh.position.copy(this.home);
      this.ballMesh.rotation.set(0, 0, 0);
      this.writeStatsToDOM(BASELINE_SPIN, BASELINE_LAUNCH, BASELINE_CARRY);
      this.trailSvg.style.opacity = '0';
      return;
    }

    // 4. Mouse → world cursor position.
    const now = performance.now();
    const stationary = now - this.lastMouseT > MOUSE_IDLE_MS;
    if (stationary) {
      // Decay velocity to 0 quickly when the mouse is still — prevents the ball
      // from being shoved by a stale velocity reading mid-idle.
      this.mouseVel.x *= 0.6;
      this.mouseVel.y *= 0.6;
    } else {
      // EMA: weight recent dx/dy at MOUSE_VEL_EMA, decay older at (1 - that).
      const dx = this.mouseScreen.x - this.lastMouseScreen.x;
      const dy = this.mouseScreen.y - this.lastMouseScreen.y;
      this.mouseVel.x = this.mouseVel.x * (1 - MOUSE_VEL_EMA) + dx * MOUSE_VEL_EMA;
      this.mouseVel.y = this.mouseVel.y * (1 - MOUSE_VEL_EMA) + dy * MOUSE_VEL_EMA;
    }
    this.lastMouseScreen.x = this.mouseScreen.x;
    this.lastMouseScreen.y = this.mouseScreen.y;

    // Map screen coords → world coords on the same plane the ball lives on.
    // We synthesize a tiny "fake DOM rect" for elementToWorld via direct math
    // (cheaper than creating a phantom element each frame).
    const ndcX = (this.mouseScreen.x / window.innerWidth) * 2 - 1;
    const ndcY = -((this.mouseScreen.y / window.innerHeight) * 2 - 1);
    this.tmpVec3.set(ndcX, ndcY, 0.5).unproject(this.camera);
    this.tmpVec3.sub(this.camera.position).normalize();
    this.tmpVec3b
      .copy(this.camera.position)
      .addScaledVector(this.tmpVec3, HERO_DEPTH);

    // 5. Drive cursor body translation. setNextKinematicTranslation tells Rapier
    //    "you're going here — interpolate the collision sweep so we catch the
    //    ball even at high mouse speed".
    if (this.cursorBody) {
      this.cursorBody.setNextKinematicTranslation({
        x: this.tmpVec3b.x,
        y: this.tmpVec3b.y,
        z: this.tmpVec3b.z,
      });
    }

    // 6. Apply cursor → ball impulse if they're touching (or near-touching).
    //    We use distance test instead of contactPair API (cheaper for a single
    //    body pair, no need to walk the contact graph). When close, push ball
    //    in the direction the cursor is moving in, scaled by AMPLIFY.
    const ballPos = this.ballBody.translation();
    const dx = ballPos.x - this.tmpVec3b.x;
    const dy = ballPos.y - this.tmpVec3b.y;
    const dz = ballPos.z - this.tmpVec3b.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    const contactDist = BALL_RADIUS + CURSOR_RADIUS;
    if (distSq < contactDist * contactDist && !stationary) {
      // Velocity in screen pixels/frame → world units/sec is fuzzy; AMPLIFY
      // is tuned empirically. Convert pixel-space velocity to world-space by
      // scaling with the inverse of the viewport-to-world scale at HERO_DEPTH.
      const fovRad = (this.camera.fov * Math.PI) / 180;
      const worldHeight = 2 * HERO_DEPTH * Math.tan(fovRad / 2);
      const pxToWorld = worldHeight / window.innerHeight;
      const vx = this.mouseVel.x * pxToWorld * AMPLIFY;
      // Screen y grows downward; world y grows up — flip.
      const vy = -this.mouseVel.y * pxToWorld * AMPLIFY;
      this.ballBody.applyImpulse({ x: vx, y: vy, z: 0 }, true);
      // Spin: cross of contact direction × velocity → angular impulse.
      // (In real life, off-center hits cause spin; we fake it.)
      const torqueScale = 0.3;
      this.ballBody.applyTorqueImpulse(
        { x: dy * vx * torqueScale, y: -dx * vy * torqueScale, z: 0 },
        true
      );
      this.lastHitT = now;
    }

    // 7. Always-on critically-damped anchor toward home.
    //    Strength ramps from 0 at hit-time to 1 by ANCHOR_RAMP_END_MS so the
    //    initial impulse "wins" and the ball flies before being pulled back.
    //    After the ramp completes, the anchor reliably resolves the ball to
    //    home regardless of where the wall-clamp left it. (No 800ms gate — the
    //    previous gated spring caused sticky-corner jitter when the ball
    //    bounced off the soft clamp before re-engagement.)
    const sinceHit = now - this.lastHitT;
    const anchorStrength = smoothstep(sinceHit, ANCHOR_RAMP_START_MS, ANCHOR_RAMP_END_MS);

    if (anchorStrength > 0.0) {
      const lv = this.ballBody.linvel();
      // Desired velocity from a position-spring: pulls ball toward home.
      const desiredVx = (this.home.x - ballPos.x) * ANCHOR_SPRING_LAMBDA * anchorStrength;
      const desiredVy = (this.home.y - ballPos.y) * ANCHOR_SPRING_LAMBDA * anchorStrength;
      const desiredVz = (this.home.z - ballPos.z) * ANCHOR_SPRING_LAMBDA * anchorStrength;
      // Critically-damped blend: lerp current linvel → desired via exp decay.
      // Larger lambda when anchorStrength is high → faster settle, no oscillation.
      const blendLambda = ANCHOR_BLEND_LAMBDA * (1 + 4 * anchorStrength);
      const newVx = damp(lv.x, desiredVx, blendLambda, dt);
      const newVy = damp(lv.y, desiredVy, blendLambda, dt);
      const newVz = damp(lv.z, desiredVz, blendLambda, dt);
      this.ballBody.setLinvel({ x: newVx, y: newVy, z: newVz }, true);
    }

    // Idle "wind on tee" noise — only after the original re-engage window so a
    // freshly-settled ball isn't immediately jiggled. (Same gate as before.)
    if (sinceHit > ANCHOR_REENGAGE_MS) {
      this.idleNoiseAccum += dt;
      if (this.idleNoiseAccum > IDLE_NOISE_PERIOD) {
        this.idleNoiseAccum = 0;
        this.ballBody.applyImpulse(
          {
            x: (Math.random() - 0.5) * IDLE_NOISE_IMPULSE,
            y: (Math.random() - 0.5) * IDLE_NOISE_IMPULSE,
            z: 0,
          },
          true
        );
      }
    }

    // 8. Sync mesh transform from physics body.
    const t = this.ballBody.translation();
    const r = this.ballBody.rotation();
    this.ballMesh.position.set(t.x, t.y, t.z);
    this.ballMesh.quaternion.set(r.x, r.y, r.z, r.w);

    // Soft physics-space clamp to the stencil window. Pure position clamp +
    // ZERO ONLY THE OUTWARD VELOCITY COMPONENT — never zero a component that's
    // already pulling the ball back inside, otherwise the anchor (which sets
    // velocity TOWARD home each frame) gets cancelled at the wall and the ball
    // wedges in the corner forever (the original sticky-jitter bug).
    const halfW = stageWorldSize.width * 0.45;
    const halfH = stageWorldSize.height * 0.45;
    const dxFromHome = t.x - this.home.x;
    const dyFromHome = t.y - this.home.y;
    const violateX = Math.abs(dxFromHome) > halfW;
    const violateY = Math.abs(dyFromHome) > halfH;
    if (violateX || violateY) {
      const cx = clamp(t.x, this.home.x - halfW, this.home.x + halfW);
      const cy = clamp(t.y, this.home.y - halfH, this.home.y + halfH);
      this.ballBody.setTranslation({ x: cx, y: cy, z: this.home.z }, true);
      const lv = this.ballBody.linvel();
      // Sign of dxFromHome tells us which wall the ball hit: +halfW if positive.
      // Only zero the outward component (sign(lv.x) === sign(dxFromHome)).
      const zeroOutwardX =
        violateX && Math.sign(lv.x) === Math.sign(dxFromHome) && lv.x !== 0;
      const zeroOutwardY =
        violateY && Math.sign(lv.y) === Math.sign(dyFromHome) && lv.y !== 0;
      this.ballBody.setLinvel(
        {
          x: zeroOutwardX ? 0 : lv.x,
          y: zeroOutwardY ? 0 : lv.y,
          z: 0,
        },
        true
      );
    }

    // Deadzone snap-to-home: if the ball is essentially at rest near home, just
    // pin it. Prevents endless sub-pixel oscillation from idle-noise + spring.
    {
      const lv = this.ballBody.linvel();
      const dxh = this.home.x - t.x;
      const dyh = this.home.y - t.y;
      const dzh = this.home.z - t.z;
      const posErr2 = dxh * dxh + dyh * dyh + dzh * dzh;
      const speed2 = lv.x * lv.x + lv.y * lv.y + lv.z * lv.z;
      if (
        anchorStrength > 0.95 &&
        posErr2 < ANCHOR_DEADZONE_POS * ANCHOR_DEADZONE_POS &&
        speed2 < ANCHOR_DEADZONE_VEL * ANCHOR_DEADZONE_VEL
      ) {
        this.ballBody.setTranslation(
          { x: this.home.x, y: this.home.y, z: this.home.z },
          true
        );
        this.ballBody.setLinvel({ x: 0, y: 0, z: 0 }, true);
      }
    }

    // 9. Stats binding.
    this.computeAndUpdateStats(now);

    // 10. Launch detection / SVG trail.
    const linvel = this.ballBody.linvel();
    const speed = Math.sqrt(linvel.x ** 2 + linvel.y ** 2 + linvel.z ** 2);
    if (speed > LAUNCH_THRESHOLD && now - this.lastHitT < 50) {
      this.fireTrail(linvel.x, linvel.y);
    }
    // Trail fades automatically via CSS opacity transition; reset opacity 0
    // after TRAIL_FADE_MS so it can fire again.
    if (now - this.lastHitT > TRAIL_FADE_MS) {
      this.trailSvg.style.opacity = '0';
    }
  }

  /** Compute SPIN/LAUNCH/CARRY targets from physics, lerp displays, write DOM. */
  private computeAndUpdateStats(now: number): void {
    if (!this.ballBody) return;
    const lv = this.ballBody.linvel();
    const av = this.ballBody.angvel();

    const isHot = now - this.lastHitT < STAT_HOT_MS;
    if (isHot) {
      // SPIN: |angvel| × 200 (rough rad/s → rpm with showmanship boost).
      const spinRpm = Math.sqrt(av.x ** 2 + av.y ** 2 + av.z ** 2) * 200;
      // LAUNCH: angle of linvel from horizontal.
      const horiz = Math.sqrt(lv.x ** 2 + lv.z ** 2);
      const launchDeg = horiz > 0.01 ? (Math.atan2(lv.y, horiz) * 180) / Math.PI : BASELINE_LAUNCH;
      // CARRY: |v|² × coeff. Empirically tuned so a strong hit reads ~280-320.
      const speed = Math.sqrt(lv.x ** 2 + lv.y ** 2 + lv.z ** 2);
      const carryYds = speed * speed * 12 + BASELINE_CARRY * 0.6;
      this.targetSpin = clamp(BASELINE_SPIN + spinRpm * 0.4, 1500, 6500);
      this.targetLaunch = clamp(launchDeg + BASELINE_LAUNCH, -5, 32);
      this.targetCarry = clamp(carryYds, 180, 380);
    } else {
      // Decay back to baseline.
      this.targetSpin = damp(this.targetSpin, BASELINE_SPIN, STAT_BASELINE_LAMBDA, 0.016);
      this.targetLaunch = damp(this.targetLaunch, BASELINE_LAUNCH, STAT_BASELINE_LAMBDA, 0.016);
      this.targetCarry = damp(this.targetCarry, BASELINE_CARRY, STAT_BASELINE_LAMBDA, 0.016);
    }

    this.dispSpin = lerp(this.dispSpin, this.targetSpin, STAT_LERP);
    this.dispLaunch = lerp(this.dispLaunch, this.targetLaunch, STAT_LERP);
    this.dispCarry = lerp(this.dispCarry, this.targetCarry, STAT_LERP);

    this.writeStatsToDOM(this.dispSpin, this.dispLaunch, this.dispCarry);
  }

  /** Direct DOM textContent updates — playbook §6 explicitly bans React/setState. */
  private writeStatsToDOM(spin: number, launchDeg: number, carry: number): void {
    if (this.statSpans.length < 3) return;
    // Format: "2,840 rpm" / "11.2°" / "262 yds" — preserve original visual style.
    this.statSpans[0].textContent = `${Math.round(spin).toLocaleString('en-US')} rpm`;
    this.statSpans[1].textContent = `${launchDeg.toFixed(1)}°`;
    this.statSpans[2].textContent = `${Math.round(carry)} yds`;
  }

  /**
   * Animate a parabolic trajectory trail in the SVG overlay. The "launch"
   * direction comes from current ball velocity; we draw a quadratic Bezier
   * from current ball position to a faked apex + landing point.
   */
  private fireTrail(vx: number, vy: number): void {
    if (!this.stageEl) return;
    // Map ball world position back to SVG-local coords (0..100 in both axes
    // because viewBox is "0 0 100 100" and preserveAspectRatio="none").
    const ballPos = this.ballBody!.translation();
    // Cheap reverse: use stage rect — find where ball is in stage-local %.
    const stageRect = this.stageEl.getBoundingClientRect();
    // Project ball world → screen px.
    this.tmpVec3.set(ballPos.x, ballPos.y, ballPos.z).project(this.camera);
    const sx = ((this.tmpVec3.x + 1) / 2) * window.innerWidth;
    const sy = ((-this.tmpVec3.y + 1) / 2) * window.innerHeight;
    const localX = ((sx - stageRect.left) / stageRect.width) * 100;
    const localY = ((sy - stageRect.top) / stageRect.height) * 100;

    // Trajectory direction follows velocity, scaled to fit inside viewbox.
    const speed = Math.sqrt(vx * vx + vy * vy) || 1;
    const len = clamp(speed * 6, 18, 60);
    const dx = (vx / speed) * len;
    const dyL = (-vy / speed) * len; // svg y is inverted vs world y

    const endX = clamp(localX + dx, 5, 95);
    const endY = clamp(localY + dyL, 5, 95);
    // Apex — bend upward (negative svg y) for parabolic feel.
    const midX = (localX + endX) / 2;
    const midY = Math.min(localY, endY) - 18;

    this.trailPath.setAttribute(
      'd',
      `M${localX.toFixed(2)} ${localY.toFixed(2)} Q${midX.toFixed(2)} ${midY.toFixed(2)} ${endX.toFixed(2)} ${endY.toFixed(2)}`
    );
    // Pop in: reset dashoffset to length, then animate to 0 via CSS transition.
    this.trailPath.style.transition = 'none';
    this.trailPath.setAttribute('stroke-dashoffset', '120');
    // Force layout flush so the next change re-triggers transition.
    void this.trailPath.getBoundingClientRect();
    this.trailPath.style.transition = 'stroke-dashoffset 0.6s ease-out';
    this.trailPath.setAttribute('stroke-dashoffset', '0');
    this.trailSvg.style.opacity = '0.85';
  }

  dispose(scene: THREE.Scene): void {
    window.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.trailSvg && this.trailSvg.parentElement) {
      this.trailSvg.parentElement.removeChild(this.trailSvg);
    }
    if (this.ballMesh) {
      scene.remove(this.ballMesh);
      // NOTE: ball geometry is the SHARED GLB cache (loadGolfBallGeometry).
      // ContactScene also references it — do NOT dispose here. Full teardown
      // is handled by `disposeSharedGolfBallAssets` at app dispose.
    }
    if (this.maskMesh) {
      scene.remove(this.maskMesh);
      this.maskMesh.geometry.dispose();
      (this.maskMesh.material as THREE.Material).dispose();
    }
    if (this.ballMaterial) this.ballMaterial.dispose();
    if (this.ballFillMaterial) this.ballFillMaterial.dispose();
    if (this.matcapTex) this.matcapTex.dispose();
    document.documentElement.classList.remove('webgl-ready');
  }
}
